"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import {
    AlertCircle,
    CalendarDays,
    FileStack,
    KeyRound,
    Loader2,
    LogIn,
    LogOut,
    Play,
    MapPin,
    ShieldCheck,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { EVENT_WHATSAPP } from "@/lib/free-mock-event"
import {
    clearStoredAccessKey,
    readStoredAccessKey,
    storeAccessKey,
} from "@/lib/free-mock-session"
import {
    RegistrationError,
    freeMockRegistration,
    type FreeMockEntryResult,
    type InhouseTestSummary,
} from "@/services/free-mock-registration"
import { inhouseMockRuntime } from "@/services/inhouse-mock-runtime"
import { toast } from "sonner"

const KEY_LENGTH = 6

export function EntryForm() {
    const [key, setKey] = useState("")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<FreeMockEntryResult | null>(null)
    // Held until the stored key has been tried, so the form does not flash up
    // before we know whether this device already has a seat.
    const [restoring, setRestoring] = useState(true)

    const enterWith = useCallback(async (candidate: string) => {
        const entry = await freeMockRegistration.enter(candidate)
        setResult(entry)
        setKey(candidate)
        // Only a key the server accepted is worth remembering.
        storeAccessKey(candidate)
        return entry
    }, [])

    // Sign in again automatically if this device already holds a good key. A
    // key that no longer works — unapproved, or already used for a completed
    // test — is dropped rather than left to fail on every visit.
    const attempted = useRef(false)
    useEffect(() => {
        if (attempted.current) return
        attempted.current = true
        const stored = readStoredAccessKey()
        if (!stored) {
            setRestoring(false)
            return
        }
        enterWith(stored)
            .catch(() => clearStoredAccessKey())
            .finally(() => setRestoring(false))
    }, [enterWith])

    const inputRef = useRef<HTMLInputElement>(null)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        if (key.length !== KEY_LENGTH) {
            setError(`Your access key is ${KEY_LENGTH} digits.`)
            inputRef.current?.focus()
            return
        }
        setLoading(true)
        try {
            await enterWith(key)
        } catch (err) {
            setError(
                err instanceof RegistrationError
                    ? err.message
                    : "That key didn't work. Please try again."
            )
            inputRef.current?.focus()
        } finally {
            setLoading(false)
        }
    }

    if (restoring) {
        return (
            <div className="flex items-center justify-center py-24" role="status">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
                <span className="sr-only">Checking your saved access key…</span>
            </div>
        )
    }

    if (result) {
        return (
            <EnteredView
                result={result}
                accessKey={key}
                onSignOut={() => {
                    clearStoredAccessKey()
                    setResult(null)
                    setKey("")
                    setError(null)
                }}
            />
        )
    }

    return (
        <div className="mx-auto max-w-md">
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
                <div className="text-center">
                    <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10">
                        <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" />
                    </span>
                    <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground">
                        Enter your access key
                    </h1>
                    <p id="key-hint" className="mt-2 text-sm text-muted-foreground">
                        The 6-digit key we emailed you once your seat was approved.
                    </p>
                </div>

                <form onSubmit={submit} noValidate className="mt-6 space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="access-key" className="sr-only">
                            Access key
                        </Label>
                        <Input
                            id="access-key"
                            ref={inputRef}
                            autoFocus
                            value={key}
                            onChange={(e) => {
                                // Digits only, capped — the field mirrors the shape of
                                // the key so a typo is obvious before submitting.
                                setKey(e.target.value.replace(/\D/g, "").slice(0, KEY_LENGTH))
                                setError(null)
                            }}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="000000"
                            aria-invalid={!!error}
                            aria-describedby={error ? "key-hint key-error" : "key-hint"}
                            className={cn(
                                "h-14 text-center font-mono text-2xl tracking-[0.5em]",
                                error && "border-destructive"
                            )}
                        />
                        {error && (
                            <p
                                id="key-error"
                                role="alert"
                                className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
                            >
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                {error}
                            </p>
                        )}
                    </div>

                    <Button
                        type="submit"
                        size="lg"
                        className="h-12 w-full text-base"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                                Checking…
                            </>
                        ) : (
                            <>
                                <LogIn className="mr-2 h-5 w-5" aria-hidden="true" />
                                Continue
                            </>
                        )}
                    </Button>
                </form>

                <p className="mt-5 text-center text-xs text-muted-foreground">
                    Haven&apos;t reserved a seat yet?{" "}
                    <Link
                        href="/in-house-mock-test#register"
                        className="rounded font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        Reserve one
                    </Link>
                    . Lost your key?{" "}
                    <a
                        href={EVENT_WHATSAPP}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="rounded font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        Message us
                    </a>
                    .
                </p>
            </div>
        </div>
    )
}

function EnteredView({
    result,
    accessKey,
    onSignOut,
}: {
    result: FreeMockEntryResult
    accessKey: string
    onSignOut: () => void
}) {
    const router = useRouter()
    const [starting, setStarting] = useState<number | null>(null)
    // Held between the click and the confirm, because starting cannot be undone.
    const [confirming, setConfirming] = useState<InhouseTestSummary | null>(null)

    // The whole view has just replaced the key form, so send focus to the top
    // of it — otherwise focus falls back to the body and a screen reader is
    // told nothing about what happened.
    const headingRef = useRef<HTMLHeadingElement>(null)
    useEffect(() => {
        headingRef.current?.focus()
    }, [])

    // A published test can be sat at any time, and starting always starts over:
    // in-house tests are not resumable, so any earlier attempt is discarded.
    const start = async (testId: number) => {
        setConfirming(null)
        setStarting(testId)
        try {
            const session = await inhouseMockRuntime.start(accessKey, testId)
            router.push(`/in-house-mock-test/live/${session.id}?key=${accessKey}`)
        } catch {
            toast.error("Could not start that test. Please try again.")
            setStarting(null)
        }
    }

    const details: Array<[string, string]> = [
        ["Name", result.full_name],
        ["Email", result.email_masked],
        ["Phone", result.phone_masked],
        ["Institution", result.institution],
        ["Previous SAT", result.previous_sat_score ? String(result.previous_sat_score) : "—"],
        [
            "Expected score",
            result.expected_sat_score ? String(result.expected_sat_score) : "—",
        ],
    ]

    return (
        <div className="mx-auto max-w-3xl space-y-6">
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                            Seat confirmed
                        </p>
                        <h1
                            ref={headingRef}
                            tabIndex={-1}
                            className="mt-3 text-2xl font-bold tracking-tight text-foreground outline-none sm:text-3xl"
                        >
                            Welcome, {result.full_name.split(" ")[0]}
                        </h1>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/50 px-4 py-2 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Access key
                        </p>
                        <p className="font-mono text-xl font-bold tracking-widest text-foreground">
                            {result.access_key}
                        </p>
                    </div>
                </div>

                <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-t border-border pt-5 text-sm">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />
                        <dt className="sr-only">Date</dt>
                        <dd className="font-semibold text-foreground">
                            {result.event_date}
                            {result.event_time ? `, ${result.event_time}` : ""}
                        </dd>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                        <dt className="sr-only">Location</dt>
                        <dd className="font-semibold text-foreground">
                            {result.location_text || "In person"}
                        </dd>
                    </div>
                </dl>
            </div>

            <section className="rounded-3xl border border-border bg-card p-6 sm:p-8">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                    Your details
                </h2>
                <dl className="mt-4 divide-y divide-border rounded-xl border border-border">
                    {details.map(([label, value]) => (
                        <div key={label} className="flex gap-4 px-4 py-2.5 text-sm">
                            <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
                            <dd className="min-w-0 flex-1 break-words font-medium text-foreground">
                                {value}
                            </dd>
                        </div>
                    ))}
                </dl>
                <p className="mt-3 text-xs text-muted-foreground">
                    Your email and phone are partly hidden here for your own protection.
                </p>
            </section>

            <section className="rounded-3xl border border-border bg-card p-6 sm:p-8">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                    Mock tests
                </h2>

                {result.tests.length === 0 ? (
                    <div className="mt-4 rounded-xl border border-dashed border-border py-12 text-center">
                        <FileStack className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
                        <p className="mt-3 font-semibold text-foreground">
                            No tests published yet
                        </p>
                        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                            Your test will appear here once it&apos;s ready. Nothing to do until
                            the day itself.
                        </p>
                    </div>
                ) : (
                    <ul className="mt-4 space-y-3">
                        {result.tests.map((t) => (
                            <li
                                key={t.id}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4"
                            >
                                <div className="min-w-0">
                                    <p className="font-semibold text-foreground">{t.name}</p>
                                    {t.description && (
                                        <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                                            {t.description}
                                        </p>
                                    )}
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        <span className="tabular-nums">{t.total_questions}</span>{" "}
                                        questions
                                    </p>
                                </div>
                                <Button
                                    className="min-h-11"
                                    disabled={starting !== null}
                                    onClick={() => setConfirming(t)}
                                >
                                    {starting === t.id ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                            Opening…
                                        </>
                                    ) : (
                                        <>
                                            <Play className="mr-2 h-4 w-4" aria-hidden="true" />
                                            Start test
                                        </>
                                    )}
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Starting is irreversible: the server abandons any sitting already
                open for this test and begins a fresh, running clock. That is
                worth one question before it happens. */}
            <Dialog
                open={confirming !== null}
                onOpenChange={(open) => !open && setConfirming(null)}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Start {confirming?.name}?</DialogTitle>
                        <DialogDescription>
                            The clock starts as soon as you continue, and the test cannot
                            be paused or resumed later. If you have already started this
                            test, that attempt ends here and you begin again from the
                            first question.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            variant="outline"
                            className="min-h-11"
                            onClick={() => setConfirming(null)}
                        >
                            Not yet
                        </Button>
                        <Button
                            className="min-h-11"
                            onClick={() => confirming && start(confirming.id)}
                        >
                            <Play className="mr-2 h-4 w-4" aria-hidden="true" />
                            Start now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="space-y-2 text-center">
                <Button variant="outline" className="min-h-11" onClick={onSignOut}>
                    <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                    Sign out
                </Button>
                <p className="text-xs text-muted-foreground">
                    Your key is remembered on this device until you sign out. Sign out if
                    you are on a shared computer.
                </p>
            </div>
        </div>
    )
}
